import { useLazyQuery, useMutation } from "@apollo/client/react";
import { CREATE_COMPANION_SESSION } from "@graphql/mutations/CreateCompanionSession";
import GET_COMPANION_SESSION from "@graphql/queries/GetCompanionSession";
import mixpanel from "mixpanel-browser";
import {
  useEffect,
  useEffectEvent,
  useMemo,
  useRef,
  useState,
  createContext,
  useContext,
} from "react";
import { useSearchParams } from "react-router-dom";

import { useAuth } from "./use-auth";
import { useDebouncedCallback } from "./use-debounced-callback";
import { useTeam } from "./use-team";
import { useWatchUserWsEvent } from "./use-watch-user-ws-event";

// Sidebar sizing constants
export const SIDEBAR_COLLAPSED_WIDTH = 64;
export const SIDEBAR_MIN_WIDTH = 210;
export const SIDEBAR_DEFAULT_WIDTH = 480;
export const SIDEBAR_SNAP_DISTANCE = 128;

const CompanionContext = createContext();

export const CompanionIntroMessage = `Hi there! We're your veterinary AI assistants:

🐘 Summi — I can help summarize patient history from uploaded PDF records!
🦔 Quill — I can help edit or improve your notes!
🐢 Sage — I can help answer any veterinary question you may have by researching on the web!

Just type your question and we'll figure out who's best to help! You can also tag us directly with @summi, @quill, or @sage.

We're still learning, and AI can make mistakes. Remember to double-check our work!`;

export const AvailableCompanions = {
  SUMMI: {
    name: "summi",
    displayName: "Summi",
    description: "Your History Companion",
    emoji: "🐘",
    welcomeMessage:
      "Hi there, I'm Summi 🐘, here to help you turn patient records into a concise patient history. Upload a PDF, then you can ask me questions about it by typing @summi!",
  },
  QUILL: {
    name: "quill",
    displayName: "Quill",
    description: "Your Writing Companion",
    emoji: "🦔",
    welcomeMessage:
      "I'm Quill 🦔, try asking me to make a change when you're editing your notes by typing @quill!",
  },
  SAGE: {
    name: "sage",
    displayName: "Sage",
    description: "Your Research Companion",
    emoji: "🐢",
    welcomeMessage:
      "I'm Sage 🐢, here to answer all your trickiest questions - just tag me in by typing @sage! It might take me a few minutes to venture off and seek the answers you’re looking for! I’m just an old tortoise after all…",
  },
  // BILL: {
  //   name: "bill",
  //   displayName: "Bill",
  //   description: "Your Billing Companion",
  //   emoji: "🦆",
  // },
};

export const AvailableCompanionTabs = {
  history: "history",
  chat: "chat",
  settings: "settings",
  help: "help",
};

export function CompanionProvider({ children }) {
  const [activeTab, _setActiveTab] = useState("history");
  const [companionSession, setCompanionSession] = useState(null);
  const [streamingMessage, setStreamingMessage] = useState({});
  const { isUserAuthenticated } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [sidebarWidth, setSidebarWidth] = useState(
    SIDEBAR_COLLAPSED_WIDTH,
  );
  const isSidebarCollapsed = sidebarWidth <= SIDEBAR_MIN_WIDTH;
  const collapseSidebar = () =>
    setSidebarWidth(SIDEBAR_COLLAPSED_WIDTH);
  const expandSidebar = () => setSidebarWidth(SIDEBAR_DEFAULT_WIDTH);

  const { currentTeam } = useTeam();
  const [creatingSession, setCreatingSession] = useState(false);

  const [createCompanionSessionMutation] = useMutation(
    CREATE_COMPANION_SESSION,
  );

  const companionSessionRef = useRef(companionSession);
  useEffect(() => {
    companionSessionRef.current = companionSession;
  }, [companionSession]);

  const setActiveTab = (newTab) => {
    mixpanel.track("Switched Companion Sidebar Tab", {
      companionSessionUuid: companionSession?.companionSessionUuid,
      newTab: newTab,
    });
    _setActiveTab(newTab);
  };

  // Clean completed entries when session changes (bounds state growth)
  const [prevCompanionSession, setPrevCompanionSession] =
    useState(companionSession);
  if (companionSession !== prevCompanionSession) {
    setPrevCompanionSession(companionSession);
    setStreamingMessage((prev) => {
      const active = Object.entries(prev).filter(
        ([_, msg]) => !msg?.isCompleted,
      );
      if (active.length === Object.keys(prev).length) return prev;
      return Object.fromEntries(active);
    });
  }

  // Derive active streaming messages — completed ones are filtered out for consumers
  const activeStreamingMessages = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(streamingMessage).filter(
          ([_, msg]) => !msg?.isCompleted,
        ),
      ),
    [streamingMessage],
  );

  // Clear session when user logs out (state-during-render)
  if (!isUserAuthenticated && companionSession !== null) {
    setCompanionSession(null);
  }

  const startCompanionSession = useEffectEvent(() => {
    expandSidebar();
    createNewSession();
  });

  useEffect(() => {
    if (!searchParams.has("companion") || !isUserAuthenticated) {
      return;
    }

    const newParams = new URLSearchParams(searchParams);
    newParams.delete("companion");
    setSearchParams(newParams, { replace: true });
    startCompanionSession();
  }, [searchParams, setSearchParams, isUserAuthenticated]);

  const [
    getCompanionSession,
    {
      refetch: refetchCompanionSession,
      called: companionSessionCalled,
    },
  ] = useLazyQuery(GET_COMPANION_SESSION, {
    fetchPolicy: "no-cache",
  });

  const refetchAndUpdateCompanionSession = async () => {
    // Don't call refetch if lazy query hasn't been executed yet
    const currentSession = companionSessionRef.current;
    if (
      !companionSessionCalled ||
      !currentSession?.companionSessionUuid
    ) {
      return;
    }
    const { data } = await refetchCompanionSession({
      companionSessionUuid: currentSession.companionSessionUuid,
    });
    setCompanionSession(data?.companionSession);
  };

  const handleSessionUpdate = useDebouncedCallback((message) => {
    const currentSession = companionSessionRef.current;
    const messageSessionUuid = message?.data?.companion_session_uuid;
    if (!currentSession?.companionSessionUuid) {
      return;
    }
    if (currentSession.companionSessionUuid === messageSessionUuid) {
      refetchAndUpdateCompanionSession();
    }
  }, 100);

  useWatchUserWsEvent({
    onMessage: handleSessionUpdate,
    eventName: "companion_session_update",
  });

  let mostRecentCompanionUsed = null;
  const companions = Object.keys(AvailableCompanions);
  const messages = companionSession?.messages;
  if (messages?.length) {
    for (let i = messages.length - 1; i >= 0; i--) {
      const message = messages[i];
      if (
        message?.companionType &&
        companions.includes(message.companionType)
      ) {
        mostRecentCompanionUsed = message.companionType;
        break;
      }

      if (message?.textContent) {
        const foundCompanion = companions.find((companion) =>
          message.textContent
            .toLowerCase()
            .includes(`@${companion.toLowerCase()}`),
        );
        if (foundCompanion) {
          mostRecentCompanionUsed = foundCompanion;
          break;
        }
      }
    }
  }

  const createNewSession = async () => {
    setCreatingSession(true);
    const startTime = Date.now();

    try {
      const { data } = await createCompanionSessionMutation({
        variables: {
          teamUuid: currentTeam?.uuid,
          sessionType: "HISTORY",
        },
      });
      const companionSessionUuid =
        data?.createCompanionSession?.companionSessionUuid;
      const { data: companionSessionData } =
        await getCompanionSession({
          variables: {
            companionSessionUuid,
          },
        });
      mixpanel.track("Companion Session Created", {
        companionSessionUuid,
      });
      setCompanionSession(companionSessionData?.companionSession);
      setActiveTab(AvailableCompanionTabs.chat);
    } catch (e) {
      mixpanel.track("Failed to create companion session", {
        error: e.message,
      });
    } finally {
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, 500 - elapsedTime);

      if (remainingTime > 0) {
        setTimeout(() => {
          setCreatingSession(false);
        }, remainingTime);
      } else {
        setCreatingSession(false);
      }
    }
  };

  return (
    <CompanionContext.Provider
      value={{
        companionSession,
        setCompanionSession,
        activeTab,
        setActiveTab,
        getCompanionSession,
        streamingMessage: activeStreamingMessages,
        setStreamingMessage,
        refetchCompanionSession: refetchAndUpdateCompanionSession,
        mostRecentCompanionUsed,
        // Sidebar collapse / expand
        sidebarWidth,
        setSidebarWidth,
        isSidebarCollapsed,
        collapseSidebar,
        expandSidebar,
        // Create new session
        creatingSession,
        createNewSession,
      }}
    >
      {children}
    </CompanionContext.Provider>
  );
}

export function useCompanion() {
  return useContext(CompanionContext);
}
